import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { Construction } from 'lucide-react'
import { systemApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { formatDate } from '@/lib/utils'

// Polled faster than the notification bell (60s): this is what lets a tab
// left open during an activation switch to the closure page — and switch
// back — on its own, without a reload.
const POLL_MS = 20_000

// /login and /auth/callback stay reachable regardless of maintenance: a
// PLATFORM_ADMIN with no session yet must still be able to sign in to turn
// it off. A non-admin who does log in during maintenance simply lands on
// this same closure page on the next check — the backend blocks every
// other API call for them anyway.
const EXEMPT_PATHS = ['/login', '/auth/callback']

function ClosurePage({ message, reopenAt }: { message: string | null; reopenAt: string | null }) {
  const { t, i18n } = useTranslation()
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-4">
        <Construction className="w-12 h-12 text-mim-blue mx-auto" />
        <h1 className="text-xl font-bold">{t('maintenance.closureTitle')}</h1>
        <p className="text-muted-foreground">{message || t('maintenance.closureBody')}</p>
        <p className="text-sm font-medium">
          {reopenAt
            ? t('maintenance.closureReopen', { date: formatDate(reopenAt, i18n.language) })
            : t('maintenance.closureNoReopen')}
        </p>
        <p className="text-xs text-muted-foreground pt-2">{t('maintenance.closureContact')}</p>
      </div>
    </div>
  )
}

export default function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const { user } = useAuthStore()

  const { data } = useQuery({
    queryKey: ['system', 'maintenance'],
    queryFn: systemApi.getMaintenance,
    refetchInterval: POLL_MS,
    retry: false,
  })

  const exempt = EXEMPT_PATHS.includes(location.pathname)
  const isPlatformAdmin = user?.role === 'PLATFORM_ADMIN'

  if (data?.active && !exempt && !isPlatformAdmin) {
    return <ClosurePage message={data.message} reopenAt={data.reopen_at} />
  }
  return <>{children}</>
}
